import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../button';
import { NativeSelect } from '../native-select';

/**
 * Generic pagination footer — page+pageSize+total driven, with classic
 * first/prev/next/last buttons and a "linhas por página" select.
 *
 * Used by table-style listings (cards / scroll-infinite views drive their
 * own loading via IntersectionObserver instead).
 *
 * Defaults are pt-BR labels; override via props if a feature needs i18n.
 */
export interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  /** 1-based current page. */
  page: number;
  /** Currently selected page size. */
  pageSize: number;
  /** Total post-filter row count (drives totalPages computation). */
  total: number;
  /** Selectable page sizes for the dropdown. Default: [10, 25, 50, 100]. */
  pageSizeOptions?: readonly number[];
  /** Called with the new 1-based page when a page button is clicked. */
  onPageChange: (page: number) => void;
  /** Called with the new page size when the dropdown changes. */
  onPageSizeChange: (pageSize: number) => void;
  /**
   * Custom labels — defaults are pt-BR. Override for i18n. Page label receives
   * the current page and total pages as a function so the consumer can format
   * it (e.g. "Page 2 of 5" vs "Página 2 de 5").
   */
  labels?: Partial<{
    rowsPerPage: string;
    pageOf: (page: number, totalPages: number) => string;
    firstPage: string;
    previousPage: string;
    nextPage: string;
    lastPage: string;
  }>;
  /** Root testid — control buttons get `<testid>-{first|prev|next|last}` suffixes. */
  'data-testid'?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const DEFAULT_LABELS = {
  rowsPerPage: 'Linhas por página',
  pageOf: (page: number, totalPages: number) => `Página ${page} de ${totalPages}`,
  firstPage: 'Primeira página',
  previousPage: 'Página anterior',
  nextPage: 'Próxima página',
  lastPage: 'Última página',
};

const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      page,
      pageSize,
      total,
      pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
      onPageChange,
      onPageSizeChange,
      labels: labelOverrides,
      className,
      'data-testid': testid,
      ...props
    },
    ref
  ) => {
    const labels = { ...DEFAULT_LABELS, ...labelOverrides };
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const isFirstPage = page <= 1;
    const isLastPage = page >= totalPages;

    return (
      <div
        ref={ref}
        data-testid={testid}
        className={cn(
          'flex flex-row items-center justify-between gap-4 border-t border-border bg-card px-6 py-3',
          className
        )}
        {...props}
      >
        <div className="flex flex-row items-center gap-2 text-sm text-muted-foreground">
          <label htmlFor={`${testid ?? 'pagination'}-rows-per-page`}>{labels.rowsPerPage}</label>
          <div className="w-20">
            <NativeSelect
              id={`${testid ?? 'pagination'}-rows-per-page`}
              data-testid={testid ? `${testid}-page-size` : undefined}
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (!Number.isNaN(next)) onPageSizeChange(next);
              }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>

        <div className="flex flex-row items-center gap-4">
          <span
            className="text-sm text-muted-foreground"
            data-testid={testid ? `${testid}-page-label` : undefined}
          >
            {labels.pageOf(page, totalPages)}
          </span>
          <div className="flex flex-row items-center gap-1">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={labels.firstPage}
              disabled={isFirstPage}
              onClick={() => onPageChange(1)}
              data-testid={testid ? `${testid}-first` : undefined}
            >
              <ChevronsLeftGlyph />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={labels.previousPage}
              disabled={isFirstPage}
              onClick={() => onPageChange(page - 1)}
              data-testid={testid ? `${testid}-prev` : undefined}
            >
              <ChevronLeftGlyph />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={labels.nextPage}
              disabled={isLastPage}
              onClick={() => onPageChange(page + 1)}
              data-testid={testid ? `${testid}-next` : undefined}
            >
              <ChevronRightGlyph />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={labels.lastPage}
              disabled={isLastPage}
              onClick={() => onPageChange(totalPages)}
              data-testid={testid ? `${testid}-last` : undefined}
            >
              <ChevronsRightGlyph />
            </Button>
          </div>
        </div>
      </div>
    );
  }
);
Pagination.displayName = 'Pagination';

// ─── Inline icon glyphs — kept private to the primitive to avoid pulling in
// a separate icon library. Match the pattern used elsewhere in `@eureca/ui`.

function ChevronLeftGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function ChevronsLeftGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="m11 17-5-5 5-5" />
      <path d="m18 17-5-5 5-5" />
    </svg>
  );
}

function ChevronsRightGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="m6 17 5-5-5-5" />
      <path d="m13 17 5-5-5-5" />
    </svg>
  );
}

export { Pagination };
